import React from 'react';

import { Skeleton } from '@mui/material';
import clsx from 'clsx';
import { useCheckWorkflowAuthorization } from 'src/hooks';
import { getIsWorkflowStepComplete, useWorkflow, WorkflowSteps } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  container: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
}));

interface WorkflowBodyProps extends React.HTMLAttributes<HTMLElement> {
  CannotViewWF: React.FC;
  NoWorkflow: React.FC;
  workflowId: UUID | null;
  showProgress?: boolean;
}

export function WorkflowBody({
  CannotViewWF,
  NoWorkflow,
  className,
  showProgress = true,
  workflowId,
}: WorkflowBodyProps): JSX.Element | null {
  const {
    loading: workflowLoading,
    steps,
    workflow,
  } = useWorkflow(workflowId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { classes } = useStyles();
  const { canViewWorkflow, loading: checkingWorkflowAccess } =
    useCheckWorkflowAuthorization(workflowId);
  const activeStep = steps.find(step => !getIsWorkflowStepComplete(step)) ?? null;
  const loading = workflowLoading || checkingWorkflowAccess;

  if (loading)
    return (
      <div className={classes.container}>
        <Skeleton height={320} variant="rectangular" width="100%" />
      </div>
    );
  if (!workflow) return <NoWorkflow />;
  if (!canViewWorkflow) return <CannotViewWF />;

  return (
    <div className={clsx(classes.container, className)}>
      <WorkflowSteps
        activeStep={activeStep}
        orderedSteps={steps}
        showProgress={showProgress}
        workflow={workflow}
      />
    </div>
  );
}
